/**
 * Slash-command parser for the chat composer. Gaming communities expect
 * a handful of "type a command, message appears with a substitution"
 * affordances. Discord ships its own catalogue; we ship a small subset
 * that earns its keep without an entire bot framework.
 *
 * Usage at the composer:
 *
 *     const parsed = parseSlashCommand(draft, { displayName });
 *     const content = parsed?.expanded ?? draft;
 *     await api.sendMessage(channelId, { content });
 *
 * Returns null when the input doesn't start with a known command, so
 * the caller can just send the raw draft.
 *
 * Available commands:
 *   /shrug [text]    — append ¯\_(ツ)_/¯ to the text
 *   /me <action>     — italic "*displayName action*" (IRC tradition)
 *   /tableflip       — (╯°□°）╯︵ ┻━┻
 *   /unflip          — ┬─┬ ノ( ゜-゜ノ)
 *   /spoiler <text>  — wrap in ||spoilers|| (rendered as blur on hover)
 *   /lenny [text]    — append ( ͡° ͜ʖ ͡°)
 *   /help            — local-only; surfaces inline help, never sent
 */

import i18n from '@/i18n';
import { expandSlashCommand } from '@bunker/shared/slashCommands';

export interface ParsedSlashCommand {
  /** Final content to send to the server. */
  expanded: string;
  /** True when the result should be sent silently (no api call). */
  consumeOnly: boolean;
  /** Optional notice the composer can toast / show inline (e.g. for /help). */
  notice?: string;
}

/**
 * Static catalogue used by the composer's slash-command popup. Order
 * here = order in the popup (most-used first). Kept in sync with the
 * parser switch below — when adding a new command, append it here too
 * so the popup surfaces it.
 */
export const SLASH_COMMANDS: Array<{
  /** Bare command without the leading slash. */
  cmd: string;
  /** Display label, including the slash and any placeholder argument. */
  label: string;
  /** One-line hint that explains the substitution. */
  description: string;
}> = [
  // `label`/`description` are getters so they re-resolve through i18n on the
  // current language without rebuilding the array (its object identity must
  // stay stable — `rankSlashCommands('')` returns this very reference).
  { cmd: 'shrug', get label() { return '/shrug'; }, get description() { return i18n.t('ui.slashCmd.shrug'); } },
  { cmd: 'me', get label() { return i18n.t('ui.slashCmd.meLabel'); }, get description() { return i18n.t('ui.slashCmd.meDesc'); } },
  { cmd: 'tableflip', get label() { return '/tableflip'; }, get description() { return '(╯°□°）╯︵ ┻━┻'; } },
  { cmd: 'unflip', get label() { return '/unflip'; }, get description() { return '┬─┬ ノ( ゜-゜ノ)'; } },
  { cmd: 'spoiler', get label() { return i18n.t('ui.slashCmd.spoilerLabel'); }, get description() { return i18n.t('ui.slashCmd.spoilerDesc'); } },
  { cmd: 'lenny', get label() { return '/lenny'; }, get description() { return i18n.t('ui.slashCmd.lenny'); } },
  { cmd: 'help', get label() { return '/help'; }, get description() { return i18n.t('ui.slashCmd.help'); } },
];

/**
 * Filter + rank the catalogue for the composer's slash popup. Empty query →
 * full catalogue in declared order; otherwise substring match with
 * command-prefix matches first, then alphabetical.
 */
export function rankSlashCommands(query: string): typeof SLASH_COMMANDS {
  const q = query.toLowerCase();
  if (!q) return SLASH_COMMANDS;
  return SLASH_COMMANDS.filter((c) => c.cmd.toLowerCase().includes(q)).sort((a, b) => {
    const ap = a.cmd.toLowerCase().startsWith(q) ? 0 : 1;
    const bp = b.cmd.toLowerCase().startsWith(q) ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return a.cmd.localeCompare(b.cmd);
  });
}

/** Inline /help text. A function (not a const) so it re-resolves through
 *  i18n.t on the active language each time /help is invoked. */
export function helpText(): string {
  return [
    i18n.t('ui.slashCmd.helpLineShrug'),
    i18n.t('ui.slashCmd.helpLineMe'),
    '/tableflip       — (╯°□°）╯︵ ┻━┻',
    '/unflip          — ┬─┬ ノ( ゜-゜ノ)',
    i18n.t('ui.slashCmd.helpLineSpoiler'),
    i18n.t('ui.slashCmd.helpLineLenny'),
    i18n.t('ui.slashCmd.helpLineHelp'),
  ].join('\n');
}

export function parseSlashCommand(
  raw: string,
  ctx: { displayName: string | null | undefined },
): ParsedSlashCommand | null {
  // Expansion logic lives in @bunker/shared/slashCommands (shared with RN); this
  // wrapper adds the web's localized /help notice + "someone" fallback.
  const result = expandSlashCommand(raw, {
    displayName: ctx.displayName,
    someoneFallback: i18n.t('ui.slashCmd.someone'),
  });
  if (!result) return null;
  return {
    expanded: result.expanded,
    consumeOnly: result.consumeOnly,
    notice: result.isHelp ? helpText() : undefined,
  };
}
